import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { X, Check, AlertCircle, Cpu, Sparkles, Hash } from "lucide-react";
import { clsx } from "clsx";
import {
  listConnectionModels,
  updateEnabledModels,
  setAIDefault,
  type AIConnection,
  type AIDefaultRole,
  type AIModelsResponse,
} from "@/api/aiConnections";

interface Props {
  connection: AIConnection;
  onClose: () => void;
}

export default function ConnectionModelsDrawer({ connection, onClose }: Props) {
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState<Set<string>>(new Set());

  const { data, isLoading, error } = useQuery<AIModelsResponse>({
    queryKey: ["ai-connection-models", connection.id],
    queryFn: () => listConnectionModels(connection.id),
    retry: false,
  });

  useEffect(() => {
    if (data) {
      setEnabled(
        new Set(data.models.filter((m) => m.enabled).map((m) => m.id)),
      );
    }
  }, [data]);

  const saveMut = useMutation({
    mutationFn: (ids: string[]) => updateEnabledModels(connection.id, ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ai-connections"] });
      queryClient.invalidateQueries({ queryKey: ["ai-defaults"] });
      queryClient.invalidateQueries({
        queryKey: ["ai-connection-models", connection.id],
      });
      onClose();
    },
  });

  const setDefaultMut = useMutation({
    mutationFn: ({ role, model }: { role: AIDefaultRole; model: string }) =>
      setAIDefault(connection.id, role, model),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ai-connections"] });
      queryClient.invalidateQueries({ queryKey: ["ai-defaults"] });
    },
  });

  const currentGenDefault =
    connection.is_default_generate ? connection.default_gen_model : null;
  const currentEmbedDefault =
    connection.is_default_embed ? connection.default_embed_model : null;

  const toggle = (id: string) => {
    setEnabled((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const enableAll = () => {
    if (data) setEnabled(new Set(data.models.map((m) => m.id)));
  };
  const disableAll = () => setEnabled(new Set());

  const errMsg =
    (error as any)?.response?.data?.detail ?? (error as any)?.message;

  return createPortal(
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="flex w-full max-w-lg flex-col bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-gray-200 px-5 py-4">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wide text-gray-400">
              {connection.provider} • Model
            </p>
            <h2 className="truncate text-lg font-bold text-gray-900">
              {connection.name}
            </h2>
            <p className="truncate text-xs text-gray-500">{connection.base_url}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-gray-400 hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {isLoading ? (
            <p className="text-sm text-gray-500">Memuat daftar model dari provider…</p>
          ) : error ? (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <div>
                <p className="font-semibold">Gagal mengambil daftar model</p>
                {errMsg && <p className="mt-1 text-xs opacity-80">{errMsg}</p>}
              </div>
            </div>
          ) : !data || data.models.length === 0 ? (
            <p className="text-sm text-gray-400">
              Provider tidak mengembalikan model apapun.
            </p>
          ) : (
            <>
              <div className="mb-3 flex items-center justify-between text-xs">
                <span className="text-gray-500">
                  {enabled.size} dari {data.models.length} dipilih
                </span>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={enableAll}
                    className="font-medium text-blue-600 hover:text-blue-700"
                  >
                    Pilih semua
                  </button>
                  <button
                    type="button"
                    onClick={disableAll}
                    className="font-medium text-gray-500 hover:text-gray-700"
                  >
                    Kosongkan
                  </button>
                </div>
              </div>
              <ul className="space-y-1.5">
                {data.models.map((m) => {
                  const isOn = enabled.has(m.id);
                  const isGenDefault = currentGenDefault === m.id;
                  const isEmbDefault = currentEmbedDefault === m.id;
                  return (
                    <li key={m.id}>
                      <div
                        className={clsx(
                          "flex items-center gap-3 rounded-lg border px-3 py-2.5 text-sm transition-colors",
                          isOn
                            ? "border-blue-300 bg-blue-50"
                            : "border-gray-200 bg-white hover:bg-gray-50",
                        )}
                      >
                        <label className="flex flex-1 cursor-pointer items-center gap-3">
                          <input
                            type="checkbox"
                            checked={isOn}
                            onChange={() => toggle(m.id)}
                            className="h-4 w-4"
                          />
                          <Cpu
                            className={clsx(
                              "h-4 w-4",
                              isOn ? "text-blue-600" : "text-gray-400",
                            )}
                          />
                          <span
                            className={clsx(
                              "flex-1 truncate font-mono",
                              isOn ? "font-semibold text-blue-900" : "text-gray-700",
                            )}
                          >
                            {m.id}
                          </span>
                          {isOn && <Check className="h-4 w-4 text-blue-600" />}
                        </label>
                        <div className="flex gap-1">
                          <button
                            type="button"
                            disabled={!isOn || setDefaultMut.isPending}
                            onClick={() =>
                              setDefaultMut.mutate({ role: "generate", model: m.id })
                            }
                            title="Jadikan default untuk generate (LLM)"
                            className={clsx(
                              "flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors",
                              isGenDefault
                                ? "bg-emerald-600 text-white"
                                : "border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-40",
                            )}
                          >
                            <Sparkles className="h-3 w-3" />
                            Gen
                          </button>
                          <button
                            type="button"
                            disabled={!isOn || setDefaultMut.isPending}
                            onClick={() =>
                              setDefaultMut.mutate({ role: "embed", model: m.id })
                            }
                            title="Jadikan default untuk embedding"
                            className={clsx(
                              "flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors",
                              isEmbDefault
                                ? "bg-emerald-600 text-white"
                                : "border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-40",
                            )}
                          >
                            <Hash className="h-3 w-3" />
                            Emb
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
              <p className="mt-3 text-xs text-gray-500">
                Tombol <span className="font-semibold">Gen</span> / <span className="font-semibold">Emb</span> hanya aktif untuk model yang
                dicentang. Pilihan default langsung tersimpan. Ingat klik <span className="font-semibold">Simpan</span> bila
                Anda mengubah daftar model yang dicentang.
              </p>
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Batal
          </button>
          <button
            type="button"
            onClick={() => saveMut.mutate(Array.from(enabled))}
            disabled={saveMut.isPending || isLoading || !!error}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {saveMut.isPending ? "Menyimpan…" : "Simpan"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
